import QRCode from 'qrcode';
import { useEffect, useMemo, useState } from 'react';
import { leafletQrOf } from '@/features/dispensing-labels/printing';
import type { LabelSheetData } from '@/features/dispensing-labels/types';

/** The leaflet QR addresses on a sheet, drawn once as images. */
export function useLeafletQrImages(sheet: LabelSheetData | null) {
  const key = (sheet?.labels ?? [])
    .map(leafletQrOf)
    .filter((url): url is string => url !== null)
    .join('\n');
  const [drawn, setDrawn] = useState<{
    key: string;
    images: Record<string, string>;
  } | null>(null);
  const urls = useMemo(() => (key ? key.split('\n') : []), [key]);

  useEffect(() => {
    if (urls.length === 0) return;
    let live = true;
    void Promise.all(
      urls.map((url) =>
        QRCode.toDataURL(url, {
          margin: 0,
          width: 240,
          errorCorrectionLevel: 'M',
        })
          .then((src) => [url, src] as const)
          .catch(() => [url, null] as const),
      ),
    ).then((entries) => {
      if (!live) return;
      const images: Record<string, string> = {};
      for (const [url, src] of entries) if (src) images[url] = src;
      setDrawn({ key, images });
    });
    return () => {
      live = false;
    };
  }, [key, urls]);

  const ready = urls.length === 0 || drawn?.key === key;
  return { images: drawn?.key === key ? drawn.images : {}, ready };
}
